import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { api, ApiError } from "@/lib/api";
import type { QuestionType } from "@/types";

interface OptionDraft {
  label: string;
  is_correct: boolean;
}

interface QuestionDraft {
  prompt: string;
  type: QuestionType;
  points: number;
  options: OptionDraft[];
}

const questionTypeLabels: Record<QuestionType, string> = {
  single_choice: "Single choice",
  multiple_choice: "Multiple choice",
  true_false: "True / False",
  short_answer: "Short answer (manual grading)",
};

function emptyQuestion(): QuestionDraft {
  return {
    prompt: "",
    type: "single_choice",
    points: 1,
    options: [
      { label: "", is_correct: true },
      { label: "", is_correct: false },
    ],
  };
}

export function CreateQuizModal({ courseId, onClose }: { courseId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [passingScore, setPassingScore] = useState(70);
  const [timeLimitMinutes, setTimeLimitMinutes] = useState(0);
  const [maxAttempts, setMaxAttempts] = useState(0);
  const [questions, setQuestions] = useState<QuestionDraft[]>([emptyQuestion()]);
  const [error, setError] = useState<string | null>(null);

  const { mutate, isPending } = useMutation({
    mutationFn: () =>
      api.post(`/api/v1.0/courses/${courseId}/quizzes`, {
        title,
        passing_score: passingScore,
        time_limit_minutes: timeLimitMinutes,
        max_attempts: maxAttempts,
        questions: questions.map((q) => ({
          ...q,
          options: q.type === "short_answer" ? [] : q.options,
        })),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["courses", courseId, "quizzes"] });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : "Failed to create quiz"),
  });

  const updateQuestion = (index: number, patch: Partial<QuestionDraft>) => {
    setQuestions((prev) => prev.map((q, i) => (i === index ? { ...q, ...patch } : q)));
  };

  const updateOption = (qIndex: number, oIndex: number, patch: Partial<OptionDraft>) => {
    setQuestions((prev) =>
      prev.map((q, i) => {
        if (i !== qIndex) return q;
        const options = q.options.map((o, j) => {
          if (j !== oIndex) return o;
          return { ...o, ...patch };
        });
        // single/true-false: enforce only one correct option
        if (patch.is_correct && q.type !== "multiple_choice") {
          return { ...q, options: options.map((o, j) => ({ ...o, is_correct: j === oIndex })) };
        }
        return { ...q, options };
      }),
    );
  };

  return (
    <Modal title="Create quiz" onClose={onClose} size="lg">
      <div className="flex flex-col gap-4">
        <Input label="Quiz title" required value={title} onChange={(e) => setTitle(e.target.value)} />
        <div className="grid grid-cols-3 gap-3">
          <Input
            label="Passing score (%)"
            type="number"
            min={0}
            max={100}
            value={passingScore}
            onChange={(e) => setPassingScore(Number(e.target.value))}
          />
          <Input
            label="Time limit (min, 0=none)"
            type="number"
            min={0}
            value={timeLimitMinutes}
            onChange={(e) => setTimeLimitMinutes(Number(e.target.value))}
          />
          <Input
            label="Max attempts (0=unlimited)"
            type="number"
            min={0}
            value={maxAttempts}
            onChange={(e) => setMaxAttempts(Number(e.target.value))}
          />
        </div>

        <div className="flex flex-col gap-4">
          {questions.map((q, qIndex) => (
            <div key={qIndex} className="rounded-lg border border-slate-200 p-4">
              <div className="mb-3 flex items-start gap-2">
                <div className="flex-1">
                  <Input
                    label={`Question ${qIndex + 1}`}
                    value={q.prompt}
                    onChange={(e) => updateQuestion(qIndex, { prompt: e.target.value })}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setQuestions((prev) => prev.filter((_, i) => i !== qIndex))}
                  className="mt-6 text-slate-400 hover:text-red-500"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="mb-3 grid grid-cols-2 gap-3">
                <div className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-slate-700">Type</label>
                  <select
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    value={q.type}
                    onChange={(e) => {
                      const type = e.target.value as QuestionType;
                      const options =
                        type === "true_false"
                          ? [
                              { label: "True", is_correct: true },
                              { label: "False", is_correct: false },
                            ]
                          : q.options;
                      updateQuestion(qIndex, { type, options });
                    }}
                  >
                    {Object.entries(questionTypeLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <Input
                  label="Points"
                  type="number"
                  min={1}
                  value={q.points}
                  onChange={(e) => updateQuestion(qIndex, { points: Number(e.target.value) })}
                />
              </div>

              {q.type !== "short_answer" && (
                <div className="flex flex-col gap-2">
                  {q.options.map((opt, oIndex) => (
                    <div key={oIndex} className="flex items-center gap-2">
                      <input
                        type={q.type === "multiple_choice" ? "checkbox" : "radio"}
                        name={`correct-${qIndex}`}
                        checked={opt.is_correct}
                        onChange={(e) => updateOption(qIndex, oIndex, { is_correct: e.target.checked })}
                      />
                      <input
                        className="flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm"
                        placeholder={`Option ${oIndex + 1}`}
                        value={opt.label}
                        disabled={q.type === "true_false"}
                        onChange={(e) =>
                          setQuestions((prev) =>
                            prev.map((qq, i) =>
                              i === qIndex
                                ? {
                                    ...qq,
                                    options: qq.options.map((oo, j) => (j === oIndex ? { ...oo, label: e.target.value } : oo)),
                                  }
                                : qq,
                            ),
                          )
                        }
                      />
                      {q.type !== "true_false" && q.options.length > 2 && (
                        <button
                          type="button"
                          onClick={() =>
                            setQuestions((prev) =>
                              prev.map((qq, i) => (i === qIndex ? { ...qq, options: qq.options.filter((_, j) => j !== oIndex) } : qq)),
                            )
                          }
                          className="text-slate-400 hover:text-red-500"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  ))}
                  {q.type !== "true_false" && (
                    <button
                      type="button"
                      className="mt-1 flex items-center gap-1 text-sm text-brand-600 hover:underline"
                      onClick={() =>
                        updateQuestion(qIndex, { options: [...q.options, { label: "", is_correct: false }] })
                      }
                    >
                      <Plus className="h-3.5 w-3.5" /> Add option
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}

          <button
            type="button"
            className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 py-2.5 text-sm text-slate-600 hover:bg-slate-50"
            onClick={() => setQuestions((prev) => [...prev, emptyQuestion()])}
          >
            <Plus className="h-4 w-4" /> Add question
          </button>
        </div>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex justify-end gap-2 border-t border-slate-100 pt-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={isPending} disabled={!title || questions.length === 0} onClick={() => mutate()}>
            Create quiz
          </Button>
        </div>
      </div>
    </Modal>
  );
}
